import { useEffect, useState, useCallback } from "react";
import styled from "@emotion/styled";
import {
  getQuestion,
  postAnswers,
  patchAnswers,
  deleteAnswers,
  deleteQuestion,
  getTypes,
  postTypes,
  deleteTypes,
  patchTypes,
} from "../../services/api";
import { useNotification } from '../../contexts/NotificationContext';

const MainContainer = styled.div`
  max-width: 700px;
  margin: 10px auto;
  font-family: "Roboto", "Segoe UI", Arial, sans-serif;
  padding: 10px 0;

  @media (max-width: 1024px) {
    max-width: 95%;
    padding: 15px;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 12px;
    margin: 5px auto;
  }

  @media (max-width: 480px) {
    padding: 8px;
    margin: 0 auto;
  }
`;

const Title = styled.h2`
  color: #2c3e50;
  text-align: center;
  margin-bottom: 15px;
  font-weight: 600;
  border-bottom: 2px solid var(--primary-color);
  padding-bottom: 8px;
  font-size: 18px;

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 20px;
    padding-bottom: 10px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 15px;
    padding-bottom: 8px;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;

  @media (max-width: 768px) {
    gap: 20px;
  }

  @media (max-width: 480px) {
    gap: 15px;
  }
`;

// Types Section Styles
const TypesContainer = styled.div`
  background: #fdfdfd;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    padding: 25px;
    margin-bottom: 25px;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    padding: 15px;
    margin-bottom: 20px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
`;

const TypesTitle = styled.h2`
  text-align: center;
  color: var(--primary-color);
  margin-bottom: 20px;
  font-size: 18px;

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 25px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 20px;
  }
`;

const LanguageSelector = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 15px;
  justify-content: center;

  @media (max-width: 480px) {
    gap: 6px;
    margin-bottom: 12px;
  }
`;

const LanguageButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #ddd;
  background: ${props => props.active ? 'var(--primary-color)' : '#fff'};
  color: ${props => props.active ? '#fff' : '#333'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  touch-action: manipulation;

  &:hover {
    background: ${props => props.active ? 'var(--primary-color)' : '#f5f5f5'};
  }

  @media (max-width: 768px) {
    padding: 8px 14px;
    font-size: 13px;
    border-radius: 5px;
  }

  @media (max-width: 480px) {
    padding: 6px 10px;
    font-size: 12px;
  }
`;

const TypeSelect = styled.select`
  width: 100%;
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #ccc;
  margin-bottom: 15px;
  font-size: 14px;
  transition: all 0.3s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb, 52, 152, 219), 0.2);
  }

  @media (max-width: 768px) {
    padding: 12px;
    font-size: 16px; /* Важно для iOS - предотвращает зум */
    margin-bottom: 18px;
  }

  @media (max-width: 480px) {
    padding: 10px;
    font-size: 16px;
    margin-bottom: 15px;
  }
`;

// Секция редактирования существующих типов
const EditSection = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  border-left: 4px solid var(--primary-color);

  @media (max-width: 768px) {
    padding: 20px;
    margin-bottom: 25px;
    border-radius: 10px;
    border-left-width: 5px;
  }

  @media (max-width: 480px) {
    padding: 15px;
    margin-bottom: 20px;
    border-radius: 8px;
    border-left-width: 4px;
  }
`;

const EditSectionTitle = styled.h3`
  color: #2c3e50;
  margin: 0 0 15px 0;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 20px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    margin-bottom: 15px;
    gap: 8px;
  }
`;

const SectionIcon = styled.span`
  font-size: 18px;

  @media (max-width: 768px) {
    font-size: 20px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

// Секция добавления новых типов
const AddSection = styled.div`
  background: #e8f5e8;
  border-radius: 8px;
  padding: 20px;
  border-left: 4px solid #28a745;

  @media (max-width: 768px) {
    padding: 25px;
    border-radius: 10px;
    border-left-width: 5px;
  }

  @media (max-width: 480px) {
    padding: 15px;
    border-radius: 8px;
    border-left-width: 4px;
  }
`;

const AddSectionTitle = styled.h3`
  color: #155724;
  margin: 0 0 10px 0;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 15px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    margin-bottom: 12px;
    gap: 8px;
  }
`;

const SectionDescription = styled.p`
  color: #6c757d;
  font-size: 13px;
  margin: 0 0 15px 0;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 18px;
    line-height: 1.5;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    margin-bottom: 15px;
  }
`;

const LanguageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 15px;
  }
`;

const LanguageField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (max-width: 768px) {
    gap: 8px;
  }

  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const LanguageLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #495057;
  text-align: center;
  padding: 4px 0;
  background: rgba(var(--primary-color-rgb, 52, 152, 219), 0.1);
  border-radius: 4px;
  border: 1px solid rgba(var(--primary-color-rgb, 52, 152, 219), 0.2);

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 6px 0;
    border-radius: 5px;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    padding: 5px 0;
  }
`;

const EditInput = styled.input`
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: all 0.3s;
  text-align: center;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb, 52, 152, 219), 0.2);
  }

  &::placeholder {
    text-align: center;
    font-size: 12px;
    color: #999;
  }

  @media (max-width: 768px) {
    padding: 12px;
    font-size: 16px; /* Важно для iOS */
    border-radius: 6px;

    &::placeholder {
      font-size: 14px;
    }
  }

  @media (max-width: 480px) {
    padding: 10px;
    font-size: 16px;

    &::placeholder {
      font-size: 13px;
    }
  }
`;

const AddInput = styled(EditInput)`
  border-color: #28a745;

  &:focus {
    border-color: #28a745;
    box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.2);
  }
`;

const ActionButtonsRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 15px;

  @media (max-width: 768px) {
    gap: 12px;
    margin-top: 20px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
    margin-top: 15px;
  }
`;

const RequiredNote = styled.div`
  background: #fff3cd;
  color: #856404;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  text-align: center;
  margin-bottom: 15px;
  border: 1px solid #ffeaa7;

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 10px 15px;
    margin-bottom: 18px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    padding: 8px 12px;
    margin-bottom: 15px;
  }
`;

const ValidationStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 12px;
  margin-top: 8px;
  color: ${props => props.valid ? '#28a745' : '#6c757d'};

  @media (max-width: 768px) {
    font-size: 13px;
    margin-top: 10px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    margin-top: 8px;
  }
`;

const NoTypesMessage = styled.p`
  text-align: center;
  color: #666;
  font-size: 14px;
  margin-bottom: 15px;
  font-style: italic;
  background: #fff3cd;
  padding: 15px;
  border-radius: 6px;
  border: 1px solid #ffeaa7;

  @media (max-width: 768px) {
    font-size: 15px;
    padding: 18px;
    margin-bottom: 20px;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    padding: 15px;
    margin-bottom: 15px;
  }
`;

const QuestionsContainer = styled.div`
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
  padding: 15px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 480px) {
    padding: 15px;
    border-radius: 6px;
  }
`;

const QuestionCard = styled.div`
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s;
  margin-bottom: 10px;
  border-left: 2px solid
    ${(props) => (props.answered ? "var(--primary-color)" : "#f39c12")};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    border-radius: 8px;
    margin-bottom: 15px;
    border-left-width: 3px;
  }

  @media (max-width: 480px) {
    border-radius: 6px;
    margin-bottom: 12px;
    border-left-width: 2px;

    &:hover {
      transform: none; /* Убираем hover эффекты на мобильных */
    }
  }
`;

const QuestionHeader = styled.div`
  padding: 10px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #ecf0f1;

  @media (max-width: 768px) {
    padding: 15px;
  }

  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const QuestionAuthor = styled.div`
  font-weight: 600;
  color: #2c3e50;
  font-size: 13px;
  margin-bottom: 3px;

  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 5px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    margin-bottom: 4px;
  }
`;

const QuestionStatus = styled.div`
  display: inline-block;
  padding: 2px 5px;
  font-size: 10px;
  border-radius: 3px;
  font-weight: 500;
  margin-top: 4px;
  background-color: ${(props) => (props.answered ? "var(--green)" : "#fef9e7")};
  color: ${(props) => (props.answered ? "var(--primary-color)" : "#f39c12")};

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 3px 8px;
    border-radius: 4px;
    margin-top: 6px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 3px;
    margin-top: 4px;
  }
`;

const QuestionContent = styled.div`
  padding: 10px;

  @media (max-width: 768px) {
    padding: 15px;
  }

  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const QuestionText = styled.p`
  color: #34495e;
  font-size: 12px;
  line-height: 1.4;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    line-height: 1.4;
    margin-bottom: 10px;
  }
`;

const AnswerSection = styled.div`
  margin-top: 8px;
  border-top: 1px dashed #ecf0f1;
  padding-top: 8px;

  @media (max-width: 768px) {
    margin-top: 12px;
    padding-top: 12px;
  }

  @media (max-width: 480px) {
    margin-top: 10px;
    padding-top: 10px;
  }
`;

const AnswerText = styled.p`
  color: #34495e;
  font-size: 11px;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 13px;
    line-height: 1.5;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 1.4;
  }
`;

const AnswerType = styled.div`
  font-size: 10px;
  color: #7f8c8d;
  margin-top: 4px;

  @media (max-width: 768px) {
    font-size: 12px;
    margin-top: 6px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    margin-top: 4px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 8px;

  @media (max-width: 768px) {
    gap: 10px;
    margin-top: 12px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
    margin-top: 10px;
  }
`;

const Button = styled.button`
  background-color: ${(props) => {
    if (props.primary) return "var(--primary-color)";
    if (props.danger) return "#e74c3c";
    if (props.success) return "#28a745";
    if (props.add) return "#28a745";
    return "#7f8c8d";
  }};
  color: white;
  padding: ${(props) => (props.small ? "4px 8px" : "8px 16px")};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  font-size: ${(props) => (props.small ? "10px" : "12px")};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  touch-action: manipulation;

  &:hover {
    background-color: ${(props) => {
    if (props.primary) return "var(--primary-color)";
    if (props.danger) return "#c0392b";
    if (props.success) return "#218838";
    if (props.add) return "#218838";
    return "#6c7a7a";
  }};
    transform: translateY(-1px);
    filter: brightness(0.9);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: ${(props) => (props.small ? "8px 12px" : "12px 20px")};
    font-size: ${(props) => (props.small ? "13px" : "14px")};
    border-radius: 6px;
    min-height: 44px; /* Минимальная высота для удобного тапа */
  }

  @media (max-width: 480px) {
    padding: ${(props) => (props.small ? "8px 10px" : "10px 16px")};
    font-size: ${(props) => (props.small ? "12px" : "13px")};
    min-height: 40px;
    flex: 1; /* На мобильных кнопки занимают всю ширину */

    &:hover {
      transform: none; /* Убираем анимацию на мобильных */
    }
  }
`;

const ErrorNotification = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 8px;
  border-radius: 4px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-left: 2px solid #dc3545;
  font-size: 12px;

  span {
    flex: 1;
  }

  button {
    background: none;
    border: none;
    color: #721c24;
    font-size: 14px;
    cursor: pointer;
    padding: 0 3px;
  }

  @media (max-width: 768px) {
    padding: 12px;
    font-size: 14px;
    margin-bottom: 15px;
    border-radius: 6px;
    border-left-width: 3px;

    button {
      font-size: 16px;
      padding: 0 5px;
    }
  }

  @media (max-width: 480px) {
    padding: 10px;
    font-size: 13px;
    margin-bottom: 12px;

    button {
      font-size: 15px;
      padding: 0 4px;
    }
  }
`;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 10px;
  color: #7f8c8d;
  font-style: italic;
  font-size: 12px;

  &:after {
    content: "";
    display: inline-block;
    width: 14px;
    height: 14px;
    margin-left: 6px;
    border: 2px solid #ecf0f1;
    border-radius: 50%;
    border-top-color: var(--primary-color);
    animation: spin 1s ease-in-out infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 768px) {
    padding: 15px;
    font-size: 14px;

    &:after {
      width: 16px;
      height: 16px;
      margin-left: 8px;
    }
  }

  @media (max-width: 480px) {
    padding: 12px;
    font-size: 13px;

    &:after {
      width: 15px;
      height: 15px;
      margin-left: 6px;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 20px 0;
  color: #7f8c8d;
  font-style: italic;
  font-size: 12px;

  @media (max-width: 768px) {
    padding: 30px 20px;
    font-size: 14px;
    line-height: 1.5;
  }

  @media (max-width: 480px) {
    padding: 25px 15px;
    font-size: 13px;
  }
`;

const AnswerForm = styled.div`
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
  padding: 15px;
  margin-top: 15px;

  @media (max-width: 768px) {
    padding: 20px;
    margin-top: 20px;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    padding: 15px;
    margin-top: 15px;
    border-radius: 6px;
  }
`;

const FormHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;

  h2 {
    margin: 0;
    color: #2c3e50;
    font-size: 16px;
  }

  button {
    background: none;
    border: none;
    color: #7f8c8d;
    font-size: 14px;
    cursor: pointer;
    padding: 3px;
    border-radius: 50%;
    touch-action: manipulation;

    &:hover {
      background-color: #f8f9fa;
    }
  }

  @media (max-width: 768px) {
    margin-bottom: 15px;

    h2 {
      font-size: 18px;
    }

    button {
      font-size: 16px;
      padding: 5px;
    }
  }

  @media (max-width: 480px) {
    margin-bottom: 12px;

    h2 {
      font-size: 16px;
    }

    button {
      font-size: 15px;
      padding: 4px;
    }
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  resize: vertical;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 12px;
  margin-bottom: 6px;
  transition: all 0.3s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb, 52, 152, 219), 0.2);
  }

  @media (max-width: 768px) {
    min-height: 100px;
    padding: 12px;
    font-size: 16px; /* Важно для iOS */
    margin-bottom: 10px;
    border-radius: 6px;
  }

  @media (max-width: 480px) {
    min-height: 90px;
    padding: 10px;
    font-size: 16px;
    margin-bottom: 8px;
  }
`;

const CharacterCount = styled.div`
  text-align: right;
  font-size: 10px;
  color: ${(props) => (props.limit ? "#e74c3c" : "#7f8c8d")};
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 12px;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    margin-bottom: 10px;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 12px;
  margin-bottom: 10px;
  transition: all 0.3s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb, 52, 152, 219), 0.2);
  }

  @media (max-width: 768px) {
    padding: 12px;
    font-size: 16px; /* Важно для iOS */
    margin-bottom: 15px;
    border-radius: 6px;
  }

  @media (max-width: 480px) {
    padding: 10px;
    font-size: 16px;
    margin-bottom: 12px;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0 10px;
  flex-wrap: wrap;
  gap: 2px;

  @media (max-width: 768px) {
    margin: 25px 0 15px;
    gap: 4px;
  }

  @media (max-width: 480px) {
    margin: 20px 0 10px;
    gap: 2px;
  }
`;

const PageButton = styled.button`
  background-color: ${(props) =>
    props.active ? "var(--primary-color)" : "#f8f9fa"};
  color: ${(props) => (props.active ? "white" : "#34495e")};
  border: ${(props) => (props.active ? "none" : "1px solid #e0e0e0")};
  padding: 4px 8px;
  margin: 0 2px;
  border-radius: 3px;
  cursor: pointer;
  font-weight: ${(props) => (props.active ? "600" : "400")};
  transition: all 0.2s;
  font-size: 11px;
  touch-action: manipulation;

  &:hover {
    background-color: ${(props) =>
    props.active ? "var(--primary-color)" : "#e9ecef"};
    transform: translateY(-1px);
    filter: ${(props) => (props.active ? "brightness(0.9)" : "none")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 14px;
    border-radius: 5px;
    margin: 0 3px;
    min-height: 36px;
    min-width: 36px;
  }

  @media (max-width: 480px) {
    padding: 6px 10px;
    font-size: 13px;
    margin: 0 2px;
    min-height: 32px;
    min-width: 32px;

    &:hover {
      transform: none;
    }
  }
`;

const EditAnswerForm = styled.div`
  margin-top: 6px;
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e0e0e0;

  @media (max-width: 768px) {
    margin-top: 10px;
    padding: 12px;
    border-radius: 6px;
  }

  @media (max-width: 480px) {
    margin-top: 8px;
    padding: 10px;
    border-radius: 4px;
  }
`;

const SectionTitle = styled.h3`
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid #eaeaea;
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 15px;
    padding-bottom: 8px;
  }

  @media (max-width: 480px) {
    font-size: 15px;
    margin-bottom: 12px;
    padding-bottom: 6px;
  }
`;

function AskLibrarian() {
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [answerText, setAnswerText] = useState("");
  const [editingAnswerId, setEditingAnswerId] = useState(null);
  const [editingAnswerText, setEditingAnswerText] = useState("");
  const [types, setTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Language and Types management state
  const [selectedId, setSelectedId] = useState("");
  const [editingType, setEditingType] = useState(false);
  const [editTranslations, setEditTranslations] = useState({
    ru: '',
    en: '',
    kz: ''
  });
  const [newTypeTranslations, setNewTypeTranslations] = useState({
    ru: '',
    en: '',
    kz: ''
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const questionsPerPage = 3;
  const { showNotification } = useNotification();
  const adminId = localStorage.getItem("adminId");

  // Languages configuration
  const languages = [
    { code: 'ru', label: 'РУ', name: 'Русский', placeholder: 'Введите на русском' },
    { code: 'en', label: 'EN', name: 'English', placeholder: 'Enter in English' },
    { code: 'kz', label: 'ҚЗ', name: 'Қазақ', placeholder: 'Қазақша енгізіңіз' }
  ];

  // Проверка валидности нового типа
  const isNewTypeValid = Object.values(newTypeTranslations).some(val => val.trim());

  // Single data loading function
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [questionResponse, typeResponse] = await Promise.all([
        getQuestion(),
        getTypes(),
      ]);

      if (questionResponse?.questions) {
        setQuestions(questionResponse.questions);
      }

      if (typeResponse?.types) {
        const allTypes = typeResponse.types || [];
        setTypes(allTypes);
        if (allTypes.length > 0) {
          const firstType = allTypes[0];
          setSelectedId(firstType.id.toString());
          // Use the new titles structure
          setEditTranslations({
            ru: firstType.titles?.ru || '',
            en: firstType.titles?.en || '',
            kz: firstType.titles?.kz || ''
          });
        } else {
          setSelectedId("");
          setEditTranslations({ ru: '', en: '', kz: '' });
        }
      }
    } catch (error) {
      console.error("Ошибка в отображении данных:", error);
      setError("Произошла ошибка при загрузке данных. Пожалуйста, попробуйте позже.");
      showNotification("Ошибка при загрузке данных", "error");
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Types management functions
  const handleSelectChange = (e) => {
    const id = e.target.value;
    setSelectedId(id);

    if (id) {
      const type = types.find((t) => t.id.toString() === id);
      if (type && type.titles) {
        setEditTranslations({
          ru: type.titles.ru || '',
          en: type.titles.en || '',
          kz: type.titles.kz || ''
        });
      } else {
        setEditTranslations({ ru: '', en: '', kz: '' });
      }
    } else {
      setEditTranslations({ ru: '', en: '', kz: '' });
    }
  };

  const handleUpdateType = async () => {
    if (!selectedId) return;

    // Check if at least one translation is provided
    const hasValidTranslation = Object.values(editTranslations).some(val => val.trim());
    if (!hasValidTranslation) {
      setError("Необходимо указать хотя бы один перевод");
      showNotification("Необходимо указать хотя бы один перевод", "warning");
      return;
    }

    try {
      await patchTypes({
        id: Number(selectedId),
        formData: { translations: editTranslations },
      });

      // Reload data to get updated types
      await loadData();
      setEditingType(false);
      setError(null);
      showNotification("Тип успешно обновлен", "success");
    } catch (error) {
      console.error("Ошибка при обновлении:", error);
      setError("Не удалось обновить тип");
      showNotification("Не удалось обновить тип", "error");
    }
  };

  const handleDeleteType = async () => {
    if (!selectedId) return;
    if (!window.confirm("Вы уверены, что хотите удалить этот тип?")) return;

    try {
      await deleteTypes(Number(selectedId));
      await loadData(); // Reload data
      showNotification("Тип успешно удален", "success");
    } catch (error) {
      console.error("Ошибка при удалении:", error);
      setError("Не удалось удалить тип");
      showNotification("Не удалось удалить тип", "error");
    }
  };

  const handleSubmitType = async (e) => {
    e.preventDefault();

    // Check if at least one translation is provided
    if (!isNewTypeValid) {
      setError("Необходимо указать хотя бы один перевод для нового типа");
      showNotification("Необходимо указать хотя бы один перевод для нового типа", "warning");
      return;
    }

    try {
      await postTypes({ translations: newTypeTranslations });
      setNewTypeTranslations({ ru: '', en: '', kz: '' });

      // Reload data to get updated types
      await loadData();
      setError(null);
      showNotification("Тип успешно создан", "success");
    } catch (error) {
      console.error("Ошибка при создании типа:", error);
      setError("Не удалось создать тип");
      showNotification("Не удалось создать тип", "error");
    }
  };

  const refreshQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getQuestion();
      if (response?.questions) {
        setQuestions(response.questions);
      }
      resetFormStates();
    } catch (error) {
      console.error("Ошибка при обновлении списка вопросов:", error);
      setError("Не удалось обновить список вопросов");
      showNotification("Не удалось обновить список вопросов", "error");
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  const resetFormStates = () => {
    setSelectedQuestion(null);
    setAnswerText("");
    setEditingAnswerId(null);
    setEditingAnswerText("");
    setSelectedTypeId("");
    setError(null);
  };

  const handleSelectQuestion = (question) => {
    setSelectedQuestion(question);
    setAnswerText("");
    setSelectedTypeId("");
    setError(null);

    // Scroll to answer form
    setTimeout(() => {
      const answerForm = document.getElementById("answerForm");
      if (answerForm) {
        answerForm.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const validateAnswerData = () => {
    if (!answerText.trim()) {
      setError("Поле ответа не может быть пустым");
      showNotification("Поле ответа не может быть пустым", "warning");
      return false;
    }
    if (!selectedTypeId) {
      setError("Пожалуйста, выберите тип ответа");
      showNotification("Пожалуйста, выберите тип ответа", "warning");
      return false;
    }
    if (!adminId) {
      setError("Необходима авторизация администратора");
      showNotification("Необходима авторизация администратора", "error");
      return false;
    }
    return true;
  };

  const handleSendAnswer = async () => {
    if (!validateAnswerData()) return;
    setLoading(true);
    setError(null);
    try {
      const formData = {
        questionId: selectedQuestion.id,
        answer: answerText.trim(),
        adminId,
        typeId: selectedTypeId,
      };
      await postAnswers(formData);
      await refreshQuestions();
      showNotification("Ответ успешно отправлен", "success");
    } catch (error) {
      console.error("Ошибка при отправке ответа:", error);
      setError("Не удалось отправить ответ. Пожалуйста, попробуйте позже.");
      showNotification("Не удалось отправить ответ", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEditAnswer = (answer) => {
    setEditingAnswerId(answer.id);
    setEditingAnswerText(answer.answer);
    setSelectedTypeId(answer.typeId || "");
    setError(null);
  };

  const validateEditData = () => {
    if (!editingAnswerText.trim()) {
      setError("Поле ответа не может быть пустым");
      showNotification("Поле ответа не может быть пустым", "warning");
      return false;
    }
    if (!selectedTypeId) {
      setError("Пожалуйста, выберите тип ответа");
      showNotification("Пожалуйста, выберите тип ответа", "warning");
      return false;
    }
    return true;
  };

  const handleSaveEditedAnswer = async () => {
    if (!validateEditData()) return;
    setLoading(true);
    setError(null);
    try {
      await patchAnswers({
        id: editingAnswerId,
        formData: {
          answer: editingAnswerText.trim(),
          typeId: selectedTypeId,
        },
      });
      await refreshQuestions();
      showNotification("Ответ успешно сохранен", "success");
    } catch (error) {
      console.error("Ошибка при редактировании ответа:", error);
      setError("Не удалось сохранить изменения. Пожалуйста, попробуйте позже.");
      showNotification("Не удалось сохранить изменения", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnswer = async (answerId) => {
    if (!window.confirm("Вы уверены, что хотите удалить этот ответ?")) return;
    setLoading(true);
    setError(null);
    try {
      await deleteAnswers(answerId);
      await refreshQuestions();
      showNotification("Ответ успешно удален", "success");
    } catch (error) {
      console.error("Ошибка при удалении ответа:", error);
      setError("Не удалось удалить ответ. Пожалуйста, попробуйте позже.");
      showNotification("Не удалось удалить ответ", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm("Вы уверены, что хотите удалить этот вопрос?")) return;
    setLoading(true);
    setError(null);
    try {
      await deleteQuestion(questionId);
      await refreshQuestions();
    } catch (error) {
      console.error("Ошибка при удалении вопроса:", error);
      setError("Не удалось удалить вопрос. Пожалуйста, попробуйте позже.");
    } finally {
      setLoading(false);
    }
  };

  // Pagination
  const indexOfLastQuestion = currentPage * questionsPerPage;
  const indexOfFirstQuestion = indexOfLastQuestion - questionsPerPage;
  const currentQuestions = questions.slice(
    indexOfFirstQuestion,
    indexOfLastQuestion
  );

  const totalPages = Math.ceil(questions.length / questionsPerPage);
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <MainContainer>
      {/* Types Management Section */}
      <TypesContainer>
        <TypesTitle>Управление типами ответов</TypesTitle>

        {/* Управление существующими типами */}
        {types.length > 0 ? (
          <EditSection>
            <EditSectionTitle>
              <SectionIcon>📋</SectionIcon>
              Управление типами
            </EditSectionTitle>

            <TypeSelect value={selectedId} onChange={handleSelectChange}>
              {types.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.titles?.ru || type.titles?.en || type.titles?.kz || 'Нет перевода'}
                </option>
              ))}
            </TypeSelect>

            {!editingType ? (
              <ActionButtonsRow>
                <Button onClick={() => setEditingType(true)} title="Изменить выбранный тип">
                  ✏️ Изменить
                </Button>
                <Button onClick={handleDeleteType} title="Удалить тип" danger>
                  🗑️ Удалить
                </Button>
              </ActionButtonsRow>
            ) : (
              <>
                <LanguageGrid>
                  {languages.map(lang => (
                    <LanguageField key={lang.code}>
                      <LanguageLabel>{lang.name}</LanguageLabel>
                      <EditInput
                        type="text"
                        value={editTranslations[lang.code] || ''}
                        onChange={(e) => setEditTranslations(prev => ({
                          ...prev,
                          [lang.code]: e.target.value
                        }))}
                        placeholder={lang.placeholder}
                      />
                    </LanguageField>
                  ))}
                </LanguageGrid>

                <ActionButtonsRow>
                  <Button onClick={handleUpdateType} title="Сохранить изменения" success>
                    💾 Сохранить
                  </Button>
                  <Button onClick={() => setEditingType(false)} title="Отменить изменения">
                    ❌ Отмена
                  </Button>
                </ActionButtonsRow>
              </>
            )}
          </EditSection>
        ) : (
          <NoTypesMessage>
            📝 Типы ответов еще не созданы. Добавьте первый тип ниже.
          </NoTypesMessage>
        )}

        {/* Добавление нового типа */}
        <AddSection>
          <AddSectionTitle>
            <SectionIcon>➕</SectionIcon>
            Добавить новый тип
          </AddSectionTitle>

          <form onSubmit={handleSubmitType}>
            <LanguageGrid>
              {languages.map(lang => (
                <LanguageField key={lang.code}>
                  <LanguageLabel>{lang.name}</LanguageLabel>
                  <AddInput
                    type="text"
                    placeholder={lang.placeholder}
                    value={newTypeTranslations[lang.code] || ''}
                    onChange={(e) => setNewTypeTranslations(prev => ({
                      ...prev,
                      [lang.code]: e.target.value
                    }))}
                  />
                </LanguageField>
              ))}
            </LanguageGrid>

            <ActionButtonsRow>
              <Button
                type="submit"
                add
                disabled={!isNewTypeValid}
                title="Создать новый тип"
              >
                ➕ Добавить тип
              </Button>
            </ActionButtonsRow>
          </form>
        </AddSection>
      </TypesContainer>

      <Title>Ответы на вопросы пользователей</Title>

      {/* Error display */}
      {error && (
        <ErrorNotification>
          <span>{error}</span>
          <button onClick={() => setError(null)}>✖</button>
        </ErrorNotification>
      )}

      <ContentContainer data-list-section>
        <QuestionsContainer>
          <SectionTitle>Список вопросов</SectionTitle>
          {loading && !questions.length ? (
            <LoadingIndicator>Загрузка вопросов...</LoadingIndicator>
          ) : currentQuestions.length > 0 ? (
            <>
              {currentQuestions.map((question) => (
                <QuestionCard key={question.id} answered={!!question.Answer}>
                  <QuestionHeader>
                    <QuestionAuthor>
                      {question.Name || "Анонимный пользователь"}{" "}
                      {question.LastName || ""}
                    </QuestionAuthor>
                    <QuestionStatus answered={!!question.Answer}>
                      {question.Answer ? "Отвечен" : "Ожидает ответа"}
                    </QuestionStatus>
                  </QuestionHeader>
                  <QuestionContent>
                    <QuestionText>
                      {question.Question || "Текст вопроса отсутствует"}
                    </QuestionText>
                    {question.Answer ? (
                      <AnswerSection>
                        {editingAnswerId === question.Answer.id ? (
                          <EditAnswerForm>
                            <TextArea
                              value={editingAnswerText}
                              onChange={(e) =>
                                setEditingAnswerText(e.target.value)
                              }
                              placeholder="Введите ответ..."
                              maxLength={1000}
                            />
                            <CharacterCount
                              limit={editingAnswerText.length > 900}
                            >
                              {editingAnswerText.length}/1000 символов
                            </CharacterCount>
                            <Select
                              value={selectedTypeId}
                              onChange={(e) =>
                                setSelectedTypeId(e.target.value)
                              }
                            >
                              <option value="">Выберите тип</option>
                              {types.map((type) => (
                                <option key={type.id} value={type.id}>
                                  {type.titles?.ru || type.titles?.en || type.titles?.kz || 'Нет перевода'}
                                </option>
                              ))}
                            </Select>
                            <ActionButtons>
                              <Button
                                success
                                onClick={handleSaveEditedAnswer}
                                disabled={loading}
                              >
                                {loading ? "Сохранение..." : "Сохранить"}
                              </Button>
                              <Button
                                onClick={() => setEditingAnswerId(null)}
                                disabled={loading}
                              >
                                Отмена
                              </Button>
                            </ActionButtons>
                          </EditAnswerForm>
                        ) : (
                          <>
                            <AnswerText>{question.Answer.answer}</AnswerText>
                            <AnswerType>
                              Тип: {question.Answer.Type?.titles?.ru || "Не указан"}
                            </AnswerType>
                            <ActionButtons>
                              <Button
                                small
                                primary
                                onClick={() =>
                                  handleEditAnswer(question.Answer)
                                }
                                disabled={loading}
                              >
                                Редактировать
                              </Button>
                              <Button
                                small
                                danger
                                onClick={() =>
                                  handleDeleteAnswer(question.Answer.id)
                                }
                                disabled={loading}
                              >
                                Удалить ответ
                              </Button>
                            </ActionButtons>
                          </>
                        )}
                      </AnswerSection>
                    ) : (
                      <ActionButtons>
                        <Button
                          primary
                          onClick={() => handleSelectQuestion(question)}
                          disabled={loading}
                        >
                          Ответить
                        </Button>
                        <Button
                          danger
                          onClick={() => handleDeleteQuestion(question.id)}
                          disabled={loading}
                        >
                          Удалить вопрос
                        </Button>
                      </ActionButtons>
                    )}
                  </QuestionContent>
                </QuestionCard>
              ))}
              {/* Pagination */}
              <PaginationContainer>
                <PageButton
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  &laquo;
                </PageButton>
                {pageNumbers.map((number) => (
                  <PageButton
                    key={number}
                    active={currentPage === number}
                    onClick={() => paginate(number)}
                  >
                    {number}
                  </PageButton>
                ))}
                <PageButton
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  &raquo;
                </PageButton>
              </PaginationContainer>
            </>
          ) : (
            <EmptyState>Вопросов пока нет.</EmptyState>
          )}
        </QuestionsContainer>

        {selectedQuestion && (
          <AnswerForm id="answerForm">
            <FormHeader>
              <h2>Ответ библиотекаря</h2>
              <button
                onClick={() => setSelectedQuestion(null)}
                disabled={loading}
              >
                ✖
              </button>
            </FormHeader>
            <QuestionText>{selectedQuestion.Question}</QuestionText>
            <TextArea
              value={answerText}
              placeholder="Введите ответ..."
              onChange={(e) => setAnswerText(e.target.value)}
              maxLength={1000}
              disabled={loading}
            />
            <CharacterCount limit={answerText.length > 900}>
              {answerText.length}/1000 символов
            </CharacterCount>
            <Select
              value={selectedTypeId}
              onChange={(e) => setSelectedTypeId(e.target.value)}
              disabled={loading}
            >
              <option value="">Выберите тип</option>
              {types.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.titles?.ru || type.titles?.en || type.titles?.kz || 'Нет перевода'}
                </option>
              ))}
            </Select>
            <Button
              success
              onClick={handleSendAnswer}
              disabled={loading || !answerText.trim() || !selectedTypeId}
            >
              {loading ? "Отправка..." : "Отправить ответ"}
            </Button>
          </AnswerForm>
        )}
      </ContentContainer>
    </MainContainer>
  );
}

export default AskLibrarian;